
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Contact, AppSettings, ProjectSection, GuestParticipant } from '../types';
import { Users, Plus, Trash2, X, Search, Sparkles, Loader2, Check, Mail, UserPlus, Filter, CheckCircle2, Circle, ArrowUp, ArrowDown, ArrowUpDown, Save, Hammer, Building2, User, LayoutGrid, List as ListIcon, ChevronDown, CheckSquare } from 'lucide-react';
import { Button } from './Button';
import { extractContactsFromImage } from '../services/geminiService';
import { ensureGlobalRole } from '../services/cloudService';
import { ParticipantSheet } from './contacts/ParticipantSheet';
import { countSelected } from '../services/participantSelection';
import { anker } from '../tutorial/anker';

interface Props {
  contacts: Contact[];
  onChange: (contacts: Contact[]) => void;
  settings?: AppSettings;
  activeSectionId?: string | null;
  sections?: ProjectSection[];
  isReadOnly?: boolean;
  /** Gaeste NUR dieser Begehung. Ohne `onParticipantsChange` bleibt die Gastfunktion aus. */
  guests?: GuestParticipant[];
  /**
   * Teilnehmer-Auswahl: Kontakte UND Gaeste in EINEM Aufruf. Getrennt gesetzt
   * ueberschreibt der zweite Aufruf den ersten, weil beide Handler ihr neues
   * `report` aus demselben Stand bauen.
   */
  onParticipantsChange?: (contacts: Contact[], guests: GuestParticipant[]) => void;
}

const STANDARD_TRADES = [
  "Abbruch & Demontage",
  "Asbestsanierung",
  "Aufzugsbau",
  "Bauleitung / SiGeKo",
  "Baureinigung",
  "Beton- & Stahlbetonbau",
  "Bodenleger",
  "Dachdecker",
  "Elektroinstallation",
  "Erdbau",
  "Fassadenbau",
  "Fensterbau",
  "Fliesenleger",
  "Garten- & Landschaftsbau",
  "Gerüstbau",
  "Glaser",
  "Heizung / Sanitär (HLS)",
  "Innenputz / Außenputz",
  "Kälte- & Klimatechnik",
  "Kampfmittelräumung",
  "Kranbetrieb",
  "Maler & Lackierer",
  "Maurer",
  "Metallbau / Schlosserei",
  "Rohbau",
  "Schreiner / Tischler",
  "Spezialtiefbau",
  "Stahlbau",
  "Straßenbau",
  "Stuckateur",
  "Tiefbau",
  "Trockenbau",
  "Wärmedämmung (WDVS)",
  "Zimmerer / Holzbau"
];

const DEFAULT_ROLES = ['Bauherr', 'SiGeKo', 'Bauleitung', 'Architekt', 'Statiker', 'Gewerk', 'Fachplaner', 'Behörde'];

// Hilfskomponente für die moderne Auswahl
const CustomPicker: React.FC<{
    value: string;
    options: string[];
    onSelect: (val: string) => void;
    placeholder: string;
    icon?: React.ReactNode;
    colorClass?: string;
    /** Ankername der Lernzentrum-Tour — wird unveraendert als `data-tutorial` gesetzt. */
    'data-tutorial'?: string;
}> = ({ value, options, onSelect, placeholder, icon, colorClass = "text-slate-600 bg-slate-100", 'data-tutorial': tutorialAnker }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="relative" ref={dropdownRef}>
            <button 
                data-tutorial={tutorialAnker}
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center justify-between w-full px-3 py-1.5 rounded-lg text-xs font-bold transition-all border border-transparent hover:border-slate-300 ${value ? colorClass : 'text-slate-400 bg-slate-50'}`}
            >
                <div className="flex items-center gap-2 truncate">
                    {icon}
                    <span className="truncate">{value || placeholder}</span>
                </div>
                <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute left-0 top-full mt-1 w-64 bg-white rounded-xl shadow-2xl border border-slate-200 z-[110] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="p-2 border-b border-slate-100 bg-slate-50">
                        <input 
                            autoFocus
                            type="text"
                            placeholder="Suchen oder neu eingeben..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && search) {
                                    onSelect(search);
                                    setIsOpen(false);
                                }
                            }}
                            className="w-full px-2 py-1 text-xs border border-slate-200 rounded outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                    </div>
                    <div className="max-h-60 overflow-y-auto p-1 custom-scrollbar">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(opt => (
                                <button
                                    key={opt}
                                    onClick={() => { onSelect(opt); setIsOpen(false); }}
                                    className={`w-full text-left px-3 py-2 text-xs rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-between group ${value === opt ? 'bg-blue-50 text-blue-700 font-bold' : 'text-slate-600'}`}
                                >
                                    {opt}
                                    {value === opt && <Check className="w-3 h-3" />}
                                </button>
                            ))
                        ) : (
                            search && (
                                <button
                                    onClick={() => { onSelect(search); setIsOpen(false); }}
                                    className="w-full text-left px-3 py-2 text-xs text-blue-600 font-bold hover:bg-blue-50 transition-colors"
                                >
                                    Neu: "{search}" übernehmen
                                </button>
                            )
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export const ContactsView: React.FC<Props> = ({ contacts, onChange, settings, activeSectionId, sections, isReadOnly = false, guests, onParticipantsChange }) => {
  const [isParticipantSheetOpen, setIsParticipantSheetOpen] = useState(false);
  const guestList = guests || [];
  const participantCount = countSelected(contacts, guestList);
  const contactAiInputRef = useRef<HTMLInputElement>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  // Fortschritts-Overlay für den KI-Scan: Phase steuert Schritte + Zielprozent.
  // Echten Fortschritt liefert die KI nicht — der Balken nähert sich asymptotisch
  // dem Phasenziel, damit auch lange PDF-Analysen sichtbar "leben".
  const [aiProgress, setAiProgress] = useState<{ phase: 'read' | 'analyze' | 'prepare'; fileName: string } | null>(null);
  const [aiPercent, setAiPercent] = useState(0);
  useEffect(() => {
    if (!aiProgress) { setAiPercent(0); return; }
    const target = aiProgress.phase === 'read' ? 15 : aiProgress.phase === 'analyze' ? 90 : 100;
    const iv = setInterval(() => {
      setAiPercent(p => Math.min(target, p + Math.max(0.4, (target - p) * 0.07)));
    }, 180);
    return () => clearInterval(iv);
  }, [aiProgress?.phase]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('All');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newContactData, setNewContactData] = useState({
    company: '',
    name: '',
    role: '',
    email: '',
    tradeType: ''
  });

  const [aiSelectionModal, setAiSelectionModal] = useState<{
    isOpen: boolean;
    extracted: any[];
    selectedIndices: Set<number>;
  }>({ isOpen: false, extracted: [], selectedIndices: new Set() });

  // Berechne verfügbare Rollen ohne Dubletten (Standard + Custom + im Projekt verwendet)
  const availableRoles = useMemo(() => {
    const customRoles = settings?.customRoles || [];
    const usedRoles = contacts.map(c => c.role).filter(Boolean);
    
    // Case-insensitive Deduplizierung
    const rolesSet = new Set<string>();
    [...DEFAULT_ROLES, ...customRoles, ...usedRoles].forEach(role => {
      if (role && role.trim()) {
        const normalized = role.trim();
        const alreadyExists = Array.from(rolesSet).some(r => r.toLowerCase() === normalized.toLowerCase());
        if (!alreadyExists) {
          rolesSet.add(normalized);
        }
      }
    });
    return Array.from(rolesSet).sort();
  }, [settings?.customRoles, contacts]);

  const availableTrades = useMemo(() => {
      const usedTrades = contacts.map(c => c.tradeType).filter(Boolean);
      const tradesSet = new Set<string>();
      [...STANDARD_TRADES, ...usedTrades].forEach(trade => {
          if (trade && trade.trim()) {
              const normalized = trade.trim();
              const alreadyExists = Array.from(tradesSet).some(t => t.toLowerCase() === normalized.toLowerCase());
              if (!alreadyExists) tradesSet.add(normalized);
          }
      });
      return Array.from(tradesSet).sort();
  }, [contacts]);

  useMemo(() => {
    return Array.from(new Set(contacts.map(c => c.company).filter(Boolean))).sort();
  }, [contacts]);

  const handleAiContactUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsAiLoading(true);
    setAiProgress({ phase: 'read', fileName: file.name });
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result as string;
        setAiProgress({ phase: 'analyze', fileName: file.name });
        try {
          const extracted = await extractContactsFromImage(base64, file.type);
          setAiProgress({ phase: 'prepare', fileName: file.name });
          // Balken sichtbar auf 100 % laufen lassen, bevor das Overlay schließt.
          await new Promise(r => setTimeout(r, 600));
          if (extracted && extracted.length > 0) {
            setAiSelectionModal({
              isOpen: true,
              extracted,
              selectedIndices: new Set(extracted.map((_, i) => i)) 
            });
          } else {
            alert("Keine Kontakte im Dokument gefunden.");
          }
        } catch (err) {
          const detail = err instanceof Error ? err.message : String(err);
          alert(`KI-Analyse fehlgeschlagen: ${detail}`);
        } finally {
          setIsAiLoading(false);
          setAiProgress(null);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setIsAiLoading(false);
      setAiProgress(null);
    }
    e.target.value = '';
  };

  const confirmAiSelection = () => {
    const sectionIds = (activeSectionId && activeSectionId !== 'all') ? [activeSectionId] : [];
    
    const toAdd = aiSelectionModal.extracted
      .filter((_, index) => aiSelectionModal.selectedIndices.has(index))
      .map((c, idx) => ({
        id: crypto.randomUUID(),
        name: c.name || '',
        company: c.company || '',
        email: c.email || '',
        phone: c.phone || '',
        address: c.address || '',
        role: c.role || '',
        tradeType: c.tradeType || '',
        present: false, 
        emailDistribution: false,
        inAdvanceNotice: true,
        sectionIds: sectionIds,
        sortOrder: contacts.length + idx
      }));

    toAdd.forEach(c => {
        if(c.role) {
             ensureGlobalRole(c.role);
        }
    });

    const merged = [...contacts, ...toAdd];
    onChange(merged);
    setAiSelectionModal({ isOpen: false, extracted: [], selectedIndices: new Set() });
  };

  const toggleSelection = (index: number) => {
    const newSet = new Set(aiSelectionModal.selectedIndices);
    if (newSet.has(index)) newSet.delete(index);
    else newSet.add(index);
    setAiSelectionModal({ ...aiSelectionModal, selectedIndices: newSet });
  };

  const openAddModal = () => {
      setNewContactData({ company: '', name: '', role: '', email: '', tradeType: '' });
      setIsAddModalOpen(true);
  };

  const saveNewContact = () => {
    const sectionIds = (activeSectionId && activeSectionId !== 'all') ? [activeSectionId] : [];

    const newContact: Contact = {
      id: crypto.randomUUID(),
      name: newContactData.name,
      company: newContactData.company,
      email: newContactData.email,
      role: newContactData.role,
      tradeType: newContactData.tradeType,
      present: false,
      emailDistribution: false,
      inAdvanceNotice: true,
      sectionIds: sectionIds,
      sortOrder: contacts.length
    };
    
    if (newContact.role) {
        ensureGlobalRole(newContact.role);
    }
    
    const newList = [...contacts, newContact];
    onChange(newList);
    setIsAddModalOpen(false);
  };

  const handleRemoveContact = (id: string) => {
    if (window.confirm("Beteiligten wirklich löschen?")) {
      const newContacts = contacts.filter(c => c.id !== id);
      onChange(newContacts);
    }
  };

  const handleUpdateContact = (id: string, field: keyof Contact, value: any) => {
    const newList = contacts.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    );
    onChange(newList);
    
    if (field === 'role' && value) {
        ensureGlobalRole(value);
    }
  };

  const toggleSectionForContact = (contactId: string, sectionId: string) => {
      const newList = contacts.map(c => {
          if (c.id === contactId) {
              const currentSections = c.sectionIds || [];
              let newSections;
              if (currentSections.includes(sectionId)) {
                  newSections = currentSections.filter(sid => sid !== sectionId);
              } else {
                  newSections = [...currentSections, sectionId];
              }
              return { ...c, sectionIds: newSections };
          }
          return c;
      });
      onChange(newList);
  };

  const filteredContacts = useMemo(() => {
    return contacts.filter(c => {
        const matchesSearch = 
            (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
            (c.company || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = filterRole === 'All' || c.role === filterRole;
        
        let matchesSection = true;
        if (activeSectionId && activeSectionId !== 'all') {
            const assignedSections = c.sectionIds || [];
            if (assignedSections.length > 0) {
                 matchesSection = assignedSections.includes(activeSectionId);
            } else {
                 matchesSection = true; 
            }
        }
        return matchesSearch && matchesRole && matchesSection;
    });
  }, [contacts, searchTerm, filterRole, activeSectionId]);

  const moveContact = (id: string, direction: -1 | 1) => {
      const index = contacts.findIndex(c => c.id === id);
      const newIndex = index + direction;
      
      if (newIndex < 0 || newIndex >= contacts.length) return;

      const newList = [...contacts];
      const [movedItem] = newList.splice(index, 1);
      newList.splice(newIndex, 0, movedItem);
      
      const reorderedList = newList.map((item, i) => ({ ...item, sortOrder: i }));
      onChange(reorderedList);
  };

  const toggleAllPresent = (status: boolean) => {
      const idsToUpdate = new Set(filteredContacts.map(c => c.id));
      const newList = contacts.map(c => 
          idsToUpdate.has(c.id) ? { ...c, present: status } : c
      );
      onChange(newList);
  };

  const toggleAllDistribution = (status: boolean) => {
      const idsToUpdate = new Set(filteredContacts.map(c => c.id));
      const newList = contacts.map(c => 
          idsToUpdate.has(c.id) ? { ...c, emailDistribution: status } : c
      );
      onChange(newList);
  };

  const toggleAllAdvanceNotice = (status: boolean) => {
      const idsToUpdate = new Set(filteredContacts.map(c => c.id));
      const newList = contacts.map(c => 
          idsToUpdate.has(c.id) ? { ...c, inAdvanceNotice: status } : c
      );
      onChange(newList);
  };

  const quickSort = (type: 'alpha' | 'role') => {
      const sorted = [...contacts].sort((a, b) => {
          if (type === 'alpha') return a.company.localeCompare(b.company);
          return a.role.localeCompare(b.role);
      }).map((c, i) => ({ ...c, sortOrder: i }));
      onChange(sorted);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto animate-fade-in pb-20">
      
      {/* Header Controls */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col lg:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-4 w-full lg:w-auto">
           <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
             <Users className="w-8 h-8" />
           </div>
           <div>
             <h2 className="text-xl font-bold text-slate-800">Projektbeteiligte</h2>
             <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">{contacts.length} Personen im Verteiler</p>
           </div>
        </div>

        <div className="flex flex-wrap gap-3 w-full lg:w-auto justify-end">
            <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
                <button onClick={() => setViewMode('cards')} className={`p-2 rounded-lg transition-all ${viewMode === 'cards' ? 'bg-white shadow text-blue-600' : 'text-slate-400 hover:text-slate-600'}`} title="Kartenansicht"><LayoutGrid className="w-5 h-5"/></button>
                <button onClick={() => setViewMode('table')} className={`p-2 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white shadow text-blue-600' : 'text-slate-400 hover:text-slate-600'}`} title="Tabellenansicht"><ListIcon className="w-5 h-5"/></button>
            </div>
            {!isReadOnly && (
                <>
                    <div className="h-10 w-px bg-slate-200 mx-1 hidden sm:block"></div>
                    <Button variant="ai" onClick={() => contactAiInputRef.current?.click()} disabled={isAiLoading} className="shadow-lg border-b-2 border-indigo-700">
                        {isAiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        KI-Scan
                    </Button>
                    <Button data-tutorial="beteiligte.neu" variant="primary" onClick={openAddModal} className="shadow-lg border-b-2 border-blue-700" icon={<UserPlus className="w-4 h-4"/>}>
                        Neu hinzufügen
                    </Button>
                    <input type="file" autoFocus={false} ref={contactAiInputRef} className="hidden" accept="image/*,application/pdf" onChange={handleAiContactUpload} />
                    {aiProgress && (
                        <div className="fixed inset-0 z-[200] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-5">
                                <div className="flex items-center gap-3">
                                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shrink-0">
                                        <Sparkles className="w-5 h-5 text-white animate-pulse" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-bold text-slate-800">Verteiler wird eingelesen</div>
                                        <div className="text-xs text-slate-500 truncate" title={aiProgress.fileName}>{aiProgress.fileName}</div>
                                    </div>
                                </div>
                                <div>
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full transition-all duration-300" style={{ width: `${aiPercent}%` }} />
                                    </div>
                                    <div className="text-right text-[11px] font-bold text-indigo-600 mt-1">{Math.round(aiPercent)} %</div>
                                </div>
                                <div className="space-y-2">
                                    {([['read', 'Datei wird gelesen'], ['analyze', 'KI analysiert das Dokument'], ['prepare', 'Kontakte werden aufbereitet']] as const).map(([key, label], i, steps) => {
                                        const activeIdx = steps.findIndex(([k]) => k === aiProgress.phase);
                                        const done = i < activeIdx;
                                        const active = i === activeIdx;
                                        return (
                                            <div key={key} className={`flex items-center gap-2 text-sm ${done ? 'text-emerald-600' : active ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>
                                                {done ? <CheckCircle2 className="w-4 h-4" /> : active ? <Loader2 className="w-4 h-4 animate-spin text-indigo-600" /> : <Circle className="w-4 h-4" />}
                                                {label}{active ? '…' : ''}
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-[11px] text-slate-400">Große PDFs können bis zu einer Minute dauern.</p>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
      </div>

      {/* Teilnehmer der Begehung — ein Griff statt der Haken quer durch die
          Tabelle. Auf dem Handy ist das der einzig praktikable Weg: die
          Tabellenansicht muss dort seitlich gescrollt werden, bevor die
          Anwesenheitsspalte ueberhaupt sichtbar ist. */}
      <button
          data-tutorial="teilnehmer.oeffnen"
          onClick={() => setIsParticipantSheetOpen(true)}
          className="w-full bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-4 flex items-center gap-3 text-left hover:border-blue-300 hover:shadow-md active:scale-[0.995] transition-all"
      >
          <div className={`p-2.5 rounded-xl border shrink-0 ${participantCount > 0 ? 'bg-blue-600 text-white border-blue-600' : 'bg-slate-100 text-slate-400 border-slate-200'}`}>
              <CheckSquare className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
              <span className="block font-bold text-slate-800 text-sm">Teilnehmer der Begehung</span>
              <span className="block text-xs text-slate-400 truncate">
                  {participantCount === 0
                      ? 'Noch niemand ausgewählt — hier auswählen, Gäste inklusive'
                      : [...guestList.map(g => g.name), ...contacts.filter(c => c.present).map(c => c.name || c.company)].filter(Boolean).join(', ')}
              </span>
          </div>
          <span className={`shrink-0 text-xs font-bold px-2.5 py-1 rounded-full ${participantCount > 0 ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'bg-slate-100 text-slate-400 border border-slate-200'}`}>
              {participantCount}
          </span>
      </button>

      {isParticipantSheetOpen && (
          <ParticipantSheet
              contacts={contacts}
              guests={guestList}
              onChange={onParticipantsChange || ((c) => onChange(c))}
              onClose={() => setIsParticipantSheetOpen(false)}
              isReadOnly={isReadOnly}
              allowGuests={!!onParticipantsChange}
          />
      )}

      {/* Filter & Sort Bar & Mass Actions */}
      <div className="flex flex-col gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4 items-center w-full">
              <div className="relative flex-1 w-full group">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                  <input 
                      type="text" 
                      placeholder="Suchen nach Name, Firma oder E-Mail..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                  />
              </div>
              
              <div className="flex items-center gap-3 w-full md:w-auto">
                  <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-xl border border-slate-200">
                      <Filter className="w-4 h-4 text-slate-400" />
                      <select 
                        value={filterRole} 
                        onChange={(e) => setFilterRole(e.target.value)}
                        className="bg-transparent text-xs font-bold text-slate-600 uppercase outline-none cursor-pointer min-w-[120px]"
                      >
                          <option value="All">Alle Rollen</option>
                          {availableRoles.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                  </div>

                  <div className="h-8 w-px bg-slate-200 mx-1"></div>
                  
                  <div className="flex gap-2">
                    <button onClick={() => quickSort('alpha')} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="Alphabetisch sortieren (Firma)"><ArrowUpDown className="w-4 h-4"/></button>
                    <button onClick={() => quickSort('role')} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" title="Nach Rolle sortieren"><Users className="w-4 h-4"/></button>
                  </div>
              </div>
          </div>
          
          {/* MASS ACTIONS BAR (MOVED TO TOP) */}
          {!isReadOnly && (
              <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-xs text-slate-400 font-medium">
                      <CheckSquare className="w-4 h-4" />
                      <span>Massen-Auswahl für gefilterte Kontakte:</span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                      <div className="flex bg-slate-50 rounded-lg border border-slate-200 p-1">
                          <span className="text-[11px] font-bold text-slate-400 px-2 flex items-center select-none">Anwesend:</span>
                          <button onClick={() => toggleAllPresent(true)} className="relative treffer-44 px-2 py-1 hover:bg-blue-100 text-blue-600 text-[11px] font-bold uppercase rounded transition-colors">Alle</button>
                          <div className="w-px bg-slate-200 my-1 mx-1"></div>
                          <button onClick={() => toggleAllPresent(false)} className="relative treffer-44 px-2 py-1 hover:bg-slate-200 text-slate-500 text-[11px] font-bold uppercase rounded transition-colors">Keine</button>
                      </div>
                      <div className="flex bg-slate-50 rounded-lg border border-slate-200 p-1">
                          <span className="text-[11px] font-bold text-slate-400 px-2 flex items-center select-none">Verteiler:</span>
                          <button onClick={() => toggleAllDistribution(true)} className="relative treffer-44 px-2 py-1 hover:bg-indigo-100 text-indigo-600 text-[11px] font-bold uppercase rounded transition-colors">Alle</button>
                          <div className="w-px bg-slate-200 my-1 mx-1"></div>
                          <button onClick={() => toggleAllDistribution(false)} className="relative treffer-44 px-2 py-1 hover:bg-slate-200 text-slate-500 text-[11px] font-bold uppercase rounded transition-colors">Keine</button>
                      </div>
                      <div className="flex bg-slate-50 rounded-lg border border-slate-200 p-1">
                          <span className="text-[11px] font-bold text-slate-400 px-2 flex items-center select-none">Vorankündigung:</span>
                          <button onClick={() => toggleAllAdvanceNotice(true)} className="relative treffer-44 px-2 py-1 hover:bg-emerald-100 text-emerald-600 text-[11px] font-bold uppercase rounded transition-colors">Alle</button>
                          <div className="w-px bg-slate-200 my-1 mx-1"></div>
                          <button onClick={() => toggleAllAdvanceNotice(false)} className="relative treffer-44 px-2 py-1 hover:bg-slate-200 text-slate-500 text-[11px] font-bold uppercase rounded transition-colors">Keine</button>
                      </div>
                  </div>
              </div>
          )}
      </div>

      {/* Main Content: Cards or Table */}
      {viewMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredContacts.length === 0 ? (
                  <div className="col-span-full py-20 text-center text-slate-400 bg-white rounded-3xl border-2 border-dashed border-slate-100">
                      <div className="bg-slate-50 p-4 rounded-full w-fit mx-auto mb-4">
                         <Users className="w-10 h-10 opacity-20" />
                      </div>
                      <p className="font-bold">Keine Kontakte gefunden</p>
                      <p className="text-xs">Versuchen Sie einen anderen Suchbegriff oder Filter.</p>
                  </div>
              ) : (
                  filteredContacts.map((contact, idx) => (
                      <div key={contact.id} className="bg-white rounded-3xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 group overflow-hidden flex flex-col">
                          <div className="bg-slate-50/80 px-5 py-3 border-b border-slate-100 flex justify-between items-center shrink-0">
                              <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-lg bg-blue-600 text-white flex items-center justify-center text-[11px] font-black shadow-sm">
                                      {idx + 1}
                                  </div>
                                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{contact.role || 'Teilnehmer'}</span>
                              </div>
                              {!isReadOnly && (
                                  <div className="flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity">
                                      <button onClick={() => moveContact(contact.id, -1)} disabled={idx === 0} className="p-1.5 hover:bg-white rounded-lg text-slate-400 hover:text-blue-600 disabled:opacity-20 transition-all"><ArrowUp className="w-4 h-4" /></button>
                                      <button onClick={() => moveContact(contact.id, 1)} disabled={idx === filteredContacts.length - 1} className="p-1.5 hover:bg-white rounded-lg text-slate-400 hover:text-blue-600 disabled:opacity-20 transition-all"><ArrowDown className="w-4 h-4" /></button>
                                  </div>
                              )}
                          </div>

                          <div className="p-5 flex-1 flex flex-col gap-4">
                              <div className="flex items-start gap-3">
                                  <div className="bg-slate-100 p-2.5 rounded-2xl text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors shrink-0">
                                      <Building2 className="w-5 h-5" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                      <input 
                                          value={contact.company} 
                                          onChange={(e) => handleUpdateContact(contact.id, 'company', e.target.value)}
                                          className="w-full font-bold text-slate-800 bg-transparent border-none focus:ring-0 p-0 text-base outline-none truncate"
                                          placeholder="Firma..."
                                          readOnly={isReadOnly}
                                      />
                                      <div className={`mt-1 ${isReadOnly ? 'pointer-events-none' : ''}`}>
                                          <CustomPicker 
                                              placeholder="Tätigkeit..." 
                                              value={contact.tradeType || ''} 
                                              options={availableTrades}
                                              onSelect={(val) => handleUpdateContact(contact.id, 'tradeType', val)}
                                              icon={<Hammer className="w-3 h-3" />}
                                              colorClass="text-indigo-700 bg-indigo-50"
                                          />
                                      </div>
                                  </div>
                              </div>

                              <div className="space-y-3 pt-2">
                                  <div className="flex items-center gap-3">
                                      <User className="w-4 h-4 text-slate-300 shrink-0" />
                                      <input 
                                          value={contact.name} 
                                          onChange={(e) => handleUpdateContact(contact.id, 'name', e.target.value)}
                                          className="w-full text-sm text-slate-600 bg-transparent border-none focus:ring-0 p-0 outline-none"
                                          placeholder="Ansprechpartner..."
                                          readOnly={isReadOnly}
                                      />
                                  </div>
                                  <div className="flex items-center gap-3">
                                      <Mail className="w-4 h-4 text-slate-300 shrink-0" />
                                      <input 
                                          value={contact.email} 
                                          onChange={(e) => handleUpdateContact(contact.id, 'email', e.target.value)}
                                          className="w-full text-xs text-slate-500 bg-transparent border-none focus:ring-0 p-0 outline-none break-all"
                                          placeholder="E-Mail Adresse..."
                                          readOnly={isReadOnly}
                                      />
                                  </div>
                              </div>
                              
                              {sections && sections.length > 0 && (
                                  <div className="pt-3 border-t border-slate-50">
                                      <span className="text-[10px] font-bold text-slate-400 uppercase block mb-2 tracking-widest">Abschnitte</span>
                                      <div className="flex flex-wrap gap-1.5">
                                          {sections.map(sec => {
                                              const isAssigned = (contact.sectionIds || []).includes(sec.id);
                                              return (
                                                  <button
                                                      key={sec.id}
                                                      onClick={() => toggleSectionForContact(contact.id, sec.id)}
                                                      className={`text-[10px] px-2 py-0.5 rounded-full border transition-all ${isAssigned ? 'bg-indigo-600 text-white border-indigo-600 font-bold shadow-sm' : 'bg-white text-slate-400 border-slate-100 hover:border-slate-300'} ${isReadOnly ? 'pointer-events-none' : ''}`}
                                                  >
                                                      {sec.name}
                                                  </button>
                                              );
                                          })}
                                      </div>
                                  </div>
                              )}
                          </div>

                          {/* Action Footer */}
                          <div className="bg-slate-50/50 p-4 border-t border-slate-100 flex justify-between items-center gap-2 group-hover:bg-white transition-colors">
                              <div className="flex gap-2">
                                  <button 
                                    onClick={() => !isReadOnly && handleUpdateContact(contact.id, 'present', !contact.present)}
                                    className={`relative treffer-44 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-bold uppercase transition-all ${contact.present ? 'bg-blue-100 text-blue-700 shadow-sm' : 'text-slate-400 hover:bg-slate-100'} ${isReadOnly ? 'pointer-events-none' : ''}`}
                                  >
                                    {contact.present ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                                    Teilnehmer
                                  </button>
                                  <button 
                                    {...anker('beteiligte.verteiler', idx === 0)}
                                    onClick={() => !isReadOnly && handleUpdateContact(contact.id, 'emailDistribution', !contact.emailDistribution)}
                                    className={`relative treffer-44 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-bold uppercase transition-all ${contact.emailDistribution ? 'bg-indigo-100 text-indigo-700 shadow-sm' : 'text-slate-400 hover:bg-slate-100'} ${isReadOnly ? 'pointer-events-none' : ''}`}
                                  >
                                    {contact.emailDistribution ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                                    Verteiler
                                  </button>
                              </div>
                              {!isReadOnly && (
                                  <button onClick={() => handleRemoveContact(contact.id)} aria-label="Kontakt entfernen" className="relative treffer-44 shrink-0 p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100">
                                      <Trash2 className="w-4 h-4" />
                                  </button>
                              )}
                          </div>
                      </div>
                  ))
              )}
          </div>
      ) : (
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full text-left border-collapse table-fixed min-w-[1000px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                    <th className="px-3 py-4 w-[60px] text-center">Pos.</th>
                    <th className="px-3 py-4 w-[20%]">Firma / Institution</th>
                    <th className="px-3 py-4 w-[16%]">Tätigkeit</th>
                    <th className="px-3 py-4 w-[15%]">Name</th>
                    <th className="px-3 py-4 w-[16%]">Rolle</th>
                    <th className="px-3 py-4 w-[15%]">E-Mail</th>
                    {sections && sections.length > 0 && <th className="px-3 py-4 w-[10%] text-center">Abschnitte</th>}
                    <th className="px-2 py-4 text-center w-[80px] whitespace-nowrap">Teilnehmer</th>
                    <th className="px-2 py-4 text-center w-[80px] whitespace-nowrap">Verteiler</th>
                    <th className="px-2 py-4 text-center w-[80px] whitespace-nowrap">Vorank.</th>
                    <th className="px-2 py-4 text-right w-[60px]">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredContacts.map((contact, idx) => (
                      <tr key={contact.id} className="hover:bg-blue-50/30 transition-colors group">
                        <td className="px-2 py-3 align-middle text-center text-xs font-bold text-slate-400">
                            {!isReadOnly ? (
                                <div className="flex flex-col items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity">
                                    <button onClick={() => moveContact(contact.id, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-blue-100 text-blue-600 disabled:opacity-20"><ArrowUp className="w-3 h-3" /></button>
                                    <button onClick={() => moveContact(contact.id, 1)} disabled={idx === filteredContacts.length - 1} className="p-1 rounded hover:bg-blue-100 text-blue-600 disabled:opacity-20"><ArrowDown className="w-3 h-3" /></button>
                                </div>
                            ) : (
                                idx + 1
                            )}
                        </td>
                        <td className="px-3 py-3 align-top">
                            <input 
                                value={contact.company} 
                                onChange={(e) => handleUpdateContact(contact.id, 'company', e.target.value)}
                                className="w-full font-bold text-slate-800 bg-transparent border-none focus:ring-0 p-0 outline-none"
                                placeholder="Firma..."
                                readOnly={isReadOnly}
                            />
                        </td>
                        <td className="px-3 py-3 align-top">
                            <div className={isReadOnly ? 'pointer-events-none' : ''}>
                                <CustomPicker 
                                    placeholder="Tätigkeit..." 
                                    value={contact.tradeType || ''} 
                                    options={availableTrades}
                                    onSelect={(val) => handleUpdateContact(contact.id, 'tradeType', val)}
                                    colorClass="text-indigo-700 bg-indigo-50/50"
                                />
                            </div>
                        </td>
                        <td className="px-3 py-3 align-top">
                            <input 
                                value={contact.name} 
                                onChange={(e) => handleUpdateContact(contact.id, 'name', e.target.value)}
                                className="w-full text-sm text-slate-700 bg-transparent border-none focus:ring-0 p-0 outline-none"
                                placeholder="Name..."
                                readOnly={isReadOnly}
                            />
                        </td>
                        <td className="px-3 py-3 align-top">
                            <div className={isReadOnly ? 'pointer-events-none' : ''}>
                                <CustomPicker 
                                    placeholder="Rolle..." 
                                    value={contact.role} 
                                    options={availableRoles}
                                    onSelect={(val) => handleUpdateContact(contact.id, 'role', val)}
                                    colorClass="text-blue-700 bg-blue-50/50"
                                />
                            </div>
                        </td>
                        <td className="px-3 py-3 align-top">
                            <input 
                                value={contact.email} 
                                onChange={(e) => handleUpdateContact(contact.id, 'email', e.target.value)}
                                className="w-full text-xs text-slate-400 bg-transparent border-none focus:ring-0 p-0 outline-none truncate"
                                placeholder="E-Mail..."
                                readOnly={isReadOnly}
                            />
                        </td>
                        {sections && sections.length > 0 && (
                            <td className="px-3 py-3 align-top text-center flex flex-wrap gap-1 justify-center">
                                {(contact.sectionIds || []).map(sid => <span key={sid} className="bg-indigo-50 text-indigo-600 text-[10px] px-1.5 py-0.5 rounded border border-indigo-100">{(sections.find(s=>s.id===sid)?.name || 'S').substring(0,3)}</span>)}
                            </td>
                        )}
                        <td className="px-3 py-3 text-center align-top"><button onClick={() => !isReadOnly && handleUpdateContact(contact.id, 'present', !contact.present)} className={`relative treffer-44 p-1 rounded-full transition-all ${contact.present ? 'text-blue-600 bg-blue-50' : 'text-slate-300 hover:text-slate-400'} ${isReadOnly ? 'pointer-events-none' : ''}`}>{contact.present ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}</button></td>
                        <td className="px-3 py-3 text-center align-top"><button onClick={() => !isReadOnly && handleUpdateContact(contact.id, 'emailDistribution', !contact.emailDistribution)} className={`relative treffer-44 p-1 rounded-full transition-all ${contact.emailDistribution ? 'text-indigo-600 bg-indigo-50' : 'text-slate-300 hover:text-slate-400'} ${isReadOnly ? 'pointer-events-none' : ''}`}>{contact.emailDistribution ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}</button></td>
                        <td className="px-3 py-3 text-center align-top"><button onClick={() => !isReadOnly && handleUpdateContact(contact.id, 'inAdvanceNotice', contact.inAdvanceNotice === false ? true : false)} className={`relative treffer-44 p-1 rounded-full transition-all ${contact.inAdvanceNotice !== false ? 'text-emerald-600 bg-emerald-50' : 'text-slate-300 hover:text-slate-400'} ${isReadOnly ? 'pointer-events-none' : ''}`}>{contact.inAdvanceNotice !== false ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}</button></td>
                        <td className="px-3 py-3 text-right align-top">
                            {!isReadOnly && (
                                <button onClick={() => handleRemoveContact(contact.id)} aria-label="Kontakt entfernen" className="relative treffer-44 text-slate-300 hover:text-red-500 p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-all">
                                    <Trash2 className="w-4 h-4"/>
                                </button>
                            )}
                        </td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
      )}
      
      {isAddModalOpen && (
          <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
               <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in">
                  <div className="bg-slate-900 text-white p-6 flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-500/20 p-2 rounded-xl border border-blue-500/30"><UserPlus className="w-6 h-6 text-blue-400" /></div>
                        <h3 className="font-bold text-lg">Neuer Beteiligter</h3>
                      </div>
                      <button onClick={() => setIsAddModalOpen(false)} className="hover:bg-white/10 p-2 rounded-full transition-all"><X className="w-5 h-5"/></button>
                  </div>
                  
                  <div className="p-8 space-y-6">
                      <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Firma / Institution *</label>
                          <div className="relative">
                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                            <input 
                                type="text"
                                list="existing-companies" 
                                value={newContactData.company}
                                onChange={(e) => setNewContactData({...newContactData, company: e.target.value})}
                                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-50 focus:border-blue-500 outline-none transition-all font-bold"
                                placeholder="Firma eingeben..."
                                autoFocus
                            />
                          </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Name</label>
                              <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                                <input 
                                    data-tutorial="beteiligte.name"
                                    type="text"
                                    value={newContactData.name}
                                    onChange={(e) => setNewContactData({...newContactData, name: e.target.value})}
                                    className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-50 outline-none text-sm"
                                    placeholder="Vorname Nachname"
                                />
                              </div>
                          </div>
                          <div className="space-y-1.5">
                              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Rolle</label>
                              <CustomPicker 
                                data-tutorial="beteiligte.rolle"
                                placeholder="Rolle wählen..." 
                                value={newContactData.role} 
                                options={availableRoles}
                                onSelect={(val) => setNewContactData({...newContactData, role: val})}
                                colorClass="text-blue-700 bg-blue-50"
                              />
                          </div>
                      </div>

                      <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Gewerk / Tätigkeit</label>
                          <CustomPicker 
                                placeholder="Tätigkeit wählen..." 
                                value={newContactData.tradeType} 
                                options={availableTrades}
                                onSelect={(val) => setNewContactData({...newContactData, tradeType: val})}
                                icon={<Hammer className="w-3 h-3" />}
                                colorClass="text-indigo-700 bg-indigo-50"
                          />
                      </div>

                      <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">E-Mail</label>
                          <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                            <input 
                                type="email"
                                value={newContactData.email}
                                onChange={(e) => setNewContactData({...newContactData, email: e.target.value})}
                                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-50 outline-none text-sm"
                                placeholder="name@firma.de"
                            />
                          </div>
                      </div>
                  </div>

                  <div className="p-6 bg-slate-50 border-t flex justify-end gap-3">
                      <Button variant="ghost" onClick={() => setIsAddModalOpen(false)}>Abbrechen</Button>
                      <Button 
                        data-tutorial="beteiligte.speichern"
                        variant="primary" 
                        onClick={saveNewContact} 
                        disabled={!newContactData.company && !newContactData.name}
                        className="shadow-lg border-b-2 border-blue-700"
                        icon={<Save className="w-4 h-4"/>}
                      >
                          Speichern
                      </Button>
                  </div>
              </div>
          </div>
      )}

      {aiSelectionModal.isOpen && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-in">
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 p-6 flex justify-between items-center text-white shrink-0">
               <div className="flex items-center gap-3">
                 <Sparkles className="w-6 h-6 animate-pulse" />
                 <h3 className="font-bold text-lg">Erkannte Beteiligte prüfen</h3>
               </div>
               <button onClick={() => setAiSelectionModal({ ...aiSelectionModal, isOpen: false })} className="hover:bg-white/10 p-2 rounded-full transition-all">
                 <X className="w-6 h-6" />
               </button>
            </div>
            <div className="p-4 bg-blue-50 text-blue-800 text-[11px] font-black uppercase tracking-widest border-b border-blue-100 flex items-center justify-between">
              <span>Bitte prüfen Sie die erkannten Daten vor dem Import.</span>
              <div className="flex gap-4">
                  <button onClick={() => setAiSelectionModal({...aiSelectionModal, selectedIndices: new Set(aiSelectionModal.extracted.map((_,i)=>i))})} className="underline hover:text-blue-600 transition-colors">Alle wählen</button>
                  <button onClick={() => setAiSelectionModal({...aiSelectionModal, selectedIndices: new Set()})} className="underline hover:text-blue-600 transition-colors">Keine</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-slate-50">
                 {aiSelectionModal.extracted.map((c, index) => (
                   <div key={index} onClick={() => toggleSelection(index)} className={`group flex items-center gap-4 p-4 rounded-2xl border-2 transition-all cursor-pointer ${aiSelectionModal.selectedIndices.has(index) ? 'border-blue-500 bg-white shadow-md' : 'border-slate-100 bg-white/50 hover:bg-white hover:border-slate-200'}`}>
                      <div className={`shrink-0 w-8 h-8 rounded-xl flex items-center justify-center transition-all ${aiSelectionModal.selectedIndices.has(index) ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-200 text-slate-400 group-hover:bg-slate-300'}`}>
                        {aiSelectionModal.selectedIndices.has(index) ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 flex-1 items-center">
                        <div className="flex flex-col min-w-0">
                           <span className="text-[10px] uppercase text-slate-400 font-black tracking-widest">Firma</span>
                           <span className="text-sm font-bold text-slate-800 truncate">{c.company || '-'}</span>
                        </div>
                        <div className="flex flex-col min-w-0">
                           <span className="text-[10px] uppercase text-slate-400 font-black tracking-widest">Name</span>
                           <span className="text-sm font-medium text-slate-600 truncate">{c.name || '-'}</span>
                        </div>
                        <div className="flex flex-col min-w-0">
                           <span className="text-[10px] uppercase text-slate-400 font-black tracking-widest">Rolle</span>
                           <span className="text-xs text-slate-500 font-bold truncate">{c.role || '-'}</span>
                        </div>
                        <div className="flex flex-col min-w-0">
                           <span className="text-[10px] uppercase text-slate-400 font-black tracking-widest">Tätigkeit</span>
                           <span className="text-xs text-indigo-600 font-black truncate">{c.tradeType || '-'}</span>
                        </div>
                        <div className="flex flex-col min-w-0">
                           <span className="text-[10px] uppercase text-slate-400 font-black tracking-widest">E-Mail</span>
                           <span className="text-[11px] text-slate-400 truncate italic">{c.email || '-'}</span>
                        </div>
                      </div>
                   </div>
                 ))}
            </div>
            <div className="p-6 bg-white border-t border-slate-100 flex justify-end gap-3 shrink-0">
               <Button variant="ghost" onClick={() => setAiSelectionModal({ ...aiSelectionModal, isOpen: false })}>Abbrechen</Button>
               <Button variant="ai" onClick={confirmAiSelection} disabled={aiSelectionModal.selectedIndices.size === 0} className="shadow-lg px-8">
                 {aiSelectionModal.selectedIndices.size} Kontakte importieren
               </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
